import { SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { ComposableCommerceEventsB2B } from '@/sdk/composable-commerce-b2b';
import {
  AcceptQuoteAction,
  CancelQuoteAction,
  CreateQuoteAction,
  DeclineQuoteAction,
  QuoteQueryAction,
  QuoteRequestsQueryAction,
  QuoteStagedQueryAction,
  RenegotiateQuoteAction,
} from '../../types/actions/QuoteActions';
import { CreateQuotePayload, RenegotiateQuotePayload } from '../../types/payloads/QuotePayloads';
import {
  AcceptQuoteQuery,
  CancelQuoteQuery,
  CreateQuoteQuery,
  DeclineQuoteQuery,
  QuoteQueryQuery,
  QuoteRequestsQueryQuery,
  RenegotiateQuoteQuery,
} from '../../types/queries/QuoteQueries';
import { Quote, QuoteRequest, StagedQuote } from '@shared/types/quote';
import { PaginatedResult } from '@shared/types/result';

export type QuoteActions = {
  createQuote: CreateQuoteAction;
  query: QuoteQueryAction;
  queryRequests: QuoteRequestsQueryAction;
  queryStaged: QuoteStagedQueryAction;
  acceptQuote: AcceptQuoteAction;
  declineQuote: DeclineQuoteAction;
  renegotiateQuote: RenegotiateQuoteAction;
  cancelQuote: CancelQuoteAction;
};

export const getQuoteActions = (sdk: SDK<ComposableCommerceEventsB2B>): QuoteActions => {
  return {
    createQuote: async (
      payload: CreateQuotePayload,
      query: CreateQuoteQuery,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      return await sdk.callAction<StagedQuote>({
        actionName: 'quote/createQuoteRequest',
        payload,
        query,
        serverOptions: options.serverOptions,
      });
    },
    query: async (query: QuoteQueryQuery, options: { serverOptions?: ServerOptions } = {}) => {
      return await sdk.callAction<PaginatedResult<Quote>>({
        actionName: 'quote/query',
        query,
        serverOptions: options.serverOptions,
      });
    },
    queryRequests: async (query: QuoteRequestsQueryQuery, options: { serverOptions?: ServerOptions } = {}) => {
      return await sdk.callAction<PaginatedResult<QuoteRequest>>({
        actionName: 'quote/queryQuoteRequests',
        query,
        serverOptions: options.serverOptions,
      });
    },
    queryStaged: async (query: QuoteQueryQuery, options: { serverOptions?: ServerOptions } = {}) => {
      return await sdk.callAction<PaginatedResult<StagedQuote>>({
        actionName: 'quote/queryQuoteStaged',
        query,
        serverOptions: options.serverOptions,
      });
    },
    acceptQuote: async (query: AcceptQuoteQuery, options: { serverOptions?: ServerOptions } = {}) => {
      return await sdk.callAction<Quote>({
        actionName: 'quote/acceptQuote',
        query,
        serverOptions: options.serverOptions,
      });
    },
    declineQuote: async (query: DeclineQuoteQuery, options: { serverOptions?: ServerOptions } = {}) => {
      return await sdk.callAction<Quote>({
        actionName: 'quote/declineQuote',
        query,
        serverOptions: options.serverOptions,
      });
    },
    renegotiateQuote: async (
      payload: RenegotiateQuotePayload,
      query: RenegotiateQuoteQuery,
      options: { serverOptions?: ServerOptions } = {},
    ) => {
      return await sdk.callAction<Quote>({
        actionName: 'quote/renegotiateQuote',
        payload,
        query,
        serverOptions: options.serverOptions,
      });
    },
    cancelQuote: async (query: CancelQuoteQuery, options: { serverOptions?: ServerOptions } = {}) => {
      return await sdk.callAction<QuoteRequest>({
        actionName: 'quote/cancelQuoteRequest',
        query,
        serverOptions: options.serverOptions,
      });
    },
  };
};
